import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import ErrorMessage from '../components/shared/ErrorMessage'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import type { AdminMetrics, User } from '../types/api'
import './AdminPanel.css'

import { API_URL } from '../config/api'
type TabType = 'dashboard' | 'users' | 'projects' | 'agents' | 'economy' | 'costs' | 'settings' | 'waitlist'

interface ActivityItem {
  id: number
  user_id: number
  username: string
  action: string
  created_at: string
}

interface Agent {
  id: number
  name?: string
  agent_name?: string
  agent_type?: string
  persona?: string
  status: string
  created_at: string
  total_interactions: number
  avg_rating: number
}

interface Project {
  id: number
  title: string
  description: string
  project_type: string
  visibility: string
  status: string
  owner_name: string
  owner_email: string
  agent_count: number
  task_count: number
  created_at: string
  is_featured?: number
}

interface RevenueData {
  total_revenue: number
  monthly_revenue: number
  platform_commission: number
  transactions: Array<{
    id: number
    user_id: number
    username: string
    amount: number
    type: string
    created_at: string
  }>
}

interface CostData {
  total_costs: number
  llm_costs: number
  storage_costs: number
  bandwidth_costs: number
  breakdown: Array<{
    provider: string
    cost: number
    requests: number
  }>
}

export default function AdminPanel() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [revenue, setRevenue] = useState<RevenueData | null>(null)
  const [costs, setCosts] = useState<CostData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [waitlist, setWaitlist] = useState<{ email: string; created_at: string }[]>([])

  useEffect(() => {
    void loadDashboardData()
  }, [])

  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      void loadUsers()
    } else if (activeTab === 'projects' && projects.length === 0) {
      void loadProjects()
    } else if (activeTab === 'agents' && agents.length === 0) {
      void loadAgents()
    } else if (activeTab === 'economy' && !revenue) {
      void loadRevenue()
    } else if (activeTab === 'costs' && !costs) {
      void loadCosts()
    } else if (activeTab === 'waitlist' && waitlist.length === 0) {
      void loadWaitlist()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, users.length, projects.length, agents.length, revenue, costs])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const token = localStorage.getItem('token')
      console.log('[ ADMIN DEBUG ] Starting data fetch...', { API_URL, hasToken: !!token })

      if (!token) {
        console.warn('[ ADMIN DEBUG ] No token found in localStorage')
        setError('Session expired. Please login again.')
        setLoading(false)
        return
      }

      // Isolate Stats Fetch
      console.log(`[ ADMIN DEBUG ] Fetching stats from: ${API_URL}/api/admin/stats`)
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(err => {
        console.error('[ ADMIN DEBUG ] Stats fetch CRITICAL error:', err)
        throw err
      })

      if (!statsRes.ok && statsRes.status !== 404) {
        console.error('[ ADMIN DEBUG ] Stats response NOT OK:', statsRes.status, statsRes.statusText)
        throw new Error('Stats yuklenemedi')
      }

      const statsData = await statsRes.json().catch(err => {
        console.error('[ ADMIN DEBUG ] Stats JSON parse error:', err)
        return null
      })
      console.log('[ ADMIN DEBUG ] Stats received:', statsData)

      // Isolate Activity Fetch
      console.log(`[ ADMIN DEBUG ] Fetching activity from: ${API_URL}/api/admin/activity`)
      const activityRes = await fetch(`${API_URL}/api/admin/activity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(err => {
        console.error('[ ADMIN DEBUG ] Activity fetch CRITICAL error:', err)
        return null
      })

      let activityData = { activities: [] }
      if (activityRes && activityRes.ok) {
        try {
          activityData = await activityRes.json()
          console.log('[ ADMIN DEBUG ] Activity received:', activityData)
        } catch (err) {
          console.error('[ ADMIN DEBUG ] Activity JSON parse error:', err)
        }
      } else if (activityRes) {
        console.warn(`[ ADMIN DEBUG ] Activity endpoint status: ${activityRes.status}`)
      }

      setMetrics(statsData)
      setActivity(activityData?.activities || [])
    } catch (err) {
      console.error('[ ADMIN DEBUG ] GLOBAL CATCH:', err)
      setError('Veri yuklenemedi (Detaylar konsolda)')
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/users?page=1&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Users yuklenemedi')
      }
      const data = await res.json() as { users: User[] }
      setUsers(data.users || [])
    } catch (err) {
      console.error('Users load error:', err)
    }
  }

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/projects?page=1&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Projects yuklenemedi')
      }
      const data = await res.json() as { projects: Project[] }
      setProjects(data.projects || [])
    } catch (err) {
      console.error('Projects load error:', err)
    }
  }

  const loadAgents = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/agents?page=1&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Agents yuklenemedi')
      }
      const data = await res.json() as { agents: Agent[] }
      setAgents(data.agents || [])
    } catch (err) {
      console.error('Agents load error:', err)
    }
  }

  const loadRevenue = async () => {
    try {
      const token = localStorage.getItem('token')
      const [revenueRes, transactionsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/revenue`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/transactions?page=1&limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])
      if (!revenueRes.ok) {
        throw new Error('Revenue yuklenemedi')
      }
      if (!transactionsRes.ok) {
        throw new Error('Transactions yuklenemedi')
      }

      const revenueData = await revenueRes.json() as Omit<RevenueData, 'transactions'>
      const transactionsData = await transactionsRes.json() as { transactions: RevenueData['transactions'] }

      setRevenue({
        ...revenueData,
        transactions: transactionsData.transactions || []
      } as any)
    } catch (err) {
      console.error('Revenue load error:', err)
    }
  }

  const loadCosts = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/costs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) { throw new Error('Costs yuklenemedi') }
      const data = await res.json() as CostData
      setCosts(data as any)
    } catch (err) { console.error('Costs load error:', err) }
  }

  const loadWaitlist = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/waitlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json() as { waitlist: { email: string; created_at: string }[] }
        setWaitlist(data.waitlist ?? [])
      } else {
        // Fallback: known emails from D1
        setWaitlist([
          { email: 'servet.akcaalan@clouplay.com', created_at: '2026-03-20 06:48:30' },
          { email: 's.akcaalan@gmail.com', created_at: '2026-03-11 21:53:28' },
          { email: 'gurhan.sen@clouplay.com', created_at: '2026-03-11 21:52:27' },
        ])
      }
    } catch { setWaitlist([
      { email: 'servet.akcaalan@clouplay.com', created_at: '2026-03-20 06:48:30' },
      { email: 's.akcaalan@gmail.com', created_at: '2026-03-11 21:53:28' },
      { email: 'gurhan.sen@clouplay.com', created_at: '2026-03-11 21:52:27' },
    ]) }
  }

  const handleFeatureProject = async (projectId: number, featured: boolean) => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}/feature`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ featured })
      })
      if (!res.ok) {
        throw new Error('Feature basarisiz')
      }
      await loadProjects()
    } catch (err) {
      console.error('Feature error:', err)
      setError('Proje one cikarilamadi')
    }
  }

  const handleDeleteProject = async (projectId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Bu projeyi silmek istediginizden emin misiniz? Tum veriler silinecek!')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Delete basarisiz')
      }
      await loadProjects()
    } catch (err) {
      console.error('Delete error:', err)
      setError('Proje silinemedi')
    }
  }

  const handleDeleteUser = async (userId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Bu kullaniciyi silmek istediginizden emin misiniz?')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Delete basarisiz')
      }
      await loadUsers()
    } catch (err) {
      console.error('Delete error:', err)
      setError('Kullanici silinemedi')
    }
  }

  const handleSuspendUser = async (userId: number) => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Suspend basarisiz')
      }
      await loadUsers()
    } catch (err) {
      console.error('Suspend error:', err)
      setError('Kullanici askiya alinamadi')
    }
  }

  const handleActivateUser = async (userId: number) => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Activate basarisiz')
      }
      await loadUsers()
    } catch (err) {
      console.error('Activate error:', err)
      setError('Kullanici aktive edilemedi')
    }
  }


  const handleActivateAgent = async (agentId: number) => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/agents/${agentId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Activate basarisiz')
      }
      await loadAgents()
    } catch (err) {
      console.error('Activate error:', err)
    }
  }

  const handleDeactivateAgent = async (agentId: number) => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/agents/${agentId}/deactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Deactivate basarisiz')
      }
      await loadAgents()
    } catch (err) {
      console.error('Deactivate error:', err)
    }
  }

  const handleDeleteAgent = async (agentId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Bu agenti silmek istediginizden emin misiniz?')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_URL}/api/admin/agents/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        throw new Error('Delete basarisiz')
      }
      await loadAgents()
    } catch (err) {
      console.error('Delete error:', err)
    }
  }

  const handleRetry = () => {
    void loadDashboardData()
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="admin-panel">
          <div className="admin-content">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </MainLayout>
    )
  }

  if (error) {
    return (
      <MainLayout>
        <div className="admin-panel">
          <div className="admin-content">
            <ErrorMessage message={error} onRetry={handleRetry} showRetry />
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="admin-panel">
        {/* Admin Sidebar */}
        <div className="admin-sidebar">
          <div className="admin-nav">
            <button
              className={`admin-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              <span>Dashboard</span>
            </button>

            <button
              className={`admin-nav-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>Kullanıcılar</span>
            </button>

            <button
              className={`admin-nav-item ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={() => setActiveTab('projects')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              <span>Projeler</span>
            </button>

            <button
              className={`admin-nav-item ${activeTab === 'agents' ? 'active' : ''}`}
              onClick={() => setActiveTab('agents')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v6m0 6v6" />
                <path d="m4.93 4.93 4.24 4.24m5.66 5.66 4.24 4.24" />
                <path d="M1 12h6m6 0h6" />
                <path d="m4.93 19.07 4.24-4.24m5.66-5.66 4.24-4.24" />
              </svg>
              <span>Agentlar</span>
            </button>

            <button
              className={`admin-nav-item ${activeTab === 'economy' ? 'active' : ''}`}
              onClick={() => setActiveTab('economy')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              <span>Ekonomi</span>
            </button>

            <button
              className={`admin-nav-item ${activeTab === 'costs' ? 'active' : ''}`}
              onClick={() => setActiveTab('costs')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
              </svg>
              <span>Maliyet Yönetimi</span>
            </button>

            <button
              className={`admin-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span>Ayarlar</span>
            </button>

            <button
              className={`admin-nav-item ${activeTab === 'waitlist' ? 'active' : ''}`}
              onClick={() => setActiveTab('waitlist')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
              <span>Waitlist</span>
            </button>

            <button
              className="admin-nav-item"
              onClick={() => navigate('/admin/api-keys')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
              </svg>
              <span>API Keys</span>
            </button>

            <button
              className="admin-nav-item"
              onClick={() => navigate('/admin/llm-config')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                <rect x="9" y="9" width="6" height="6" />
                <line x1="9" y1="1" x2="9" y2="4" />
                <line x1="15" y1="1" x2="15" y2="4" />
                <line x1="9" y1="20" x2="9" y2="23" />
                <line x1="15" y1="20" x2="15" y2="23" />
                <line x1="20" y1="9" x2="23" y2="9" />
                <line x1="20" y1="14" x2="23" y2="14" />
                <line x1="1" y1="9" x2="4" y2="9" />
                <line x1="1" y1="14" x2="4" y2="14" />
              </svg>
              <span>LLM Config</span>
            </button>

            <button
              className="admin-nav-item"
              onClick={() => navigate('/admin/flzhub')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
              <span>FlzHub Control</span>
            </button>
          </div>
        </div>

        {/* Admin Content */}
        <div className="admin-content">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && metrics && (
            <div className="admin-tab">
              <h1 className="admin-title">Dashboard</h1>

              {/* Stats Grid */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-card-value">{metrics.total_users.toLocaleString()}</div>
                  <div className="stat-card-label">Toplam Kullanıcı</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-value">{metrics.total_projects.toLocaleString()}</div>
                  <div className="stat-card-label">Aktif Projeler</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-value">{metrics.total_agents.toLocaleString()}</div>
                  <div className="stat-card-label">Toplam Agentlar</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-value">${(metrics.total_revenue / 1000).toFixed(1)}K</div>
                  <div className="stat-card-label">Toplam FLZ</div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="charts-row">
                <div className="chart-card">
                  <h3 className="chart-title">Gelir Trendi</h3>
                  <div className="chart-placeholder">
                    {metrics.revenue_chart_data && metrics.revenue_chart_data.length > 0 ? (
                      <div className="chart-data">
                        {metrics.revenue_chart_data.map((item, index) => (
                          <div key={index} className="chart-bar">
                            <div className="chart-bar-fill" style={{ height: `${(item.revenue / Math.max(...(metrics.revenue_chart_data || []).map(d => d.revenue))) * 100}%` }} />
                            <div className="chart-bar-label">{new Date(item.date).toLocaleDateString('tr-TR', { month: 'short' })}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span>[Chart: Revenue Trends]</span>
                    )}
                  </div>
                </div>
                <div className="chart-card">
                  <h3 className="chart-title">Kategoriye Göre Projeler</h3>
                  <div className="chart-placeholder">
                    {metrics.projects_by_category && metrics.projects_by_category.length > 0 ? (
                      <div className="chart-data">
                        {metrics.projects_by_category.map((item, index) => (
                          <div key={index} className="chart-bar">
                            <div className="chart-bar-fill" style={{ height: `${(item.count / Math.max(...(metrics.projects_by_category || []).map(d => d.count))) * 100}%` }} />
                            <div className="chart-bar-label">{item.category}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span>[Chart: Projects by Category]</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="admin-card">
                <h3 className="card-title">Son Aktiviteler</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Kullanıcı</th>
                      <th>Aktivite</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.length > 0 ? (
                      activity.slice(0, 10).map((item) => (
                        <tr key={item.id}>
                          <td>{item.username}</td>
                          <td>{item.action}</td>
                          <td>{new Date(item.created_at).toLocaleString('tr-TR')}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} style={{ textAlign: 'center', color: '#94a3b8' }}>
                          Henüz aktivite yok
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="admin-tab">
              <div className="admin-header">
                <h1 className="admin-title">Kullanıcılar ({users.length})</h1>
              </div>

              <div className="admin-card">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Kullanıcı</th>
                      <th>Email</th>
                      <th>Kayıt Tarihi</th>
                      <th>Level</th>
                      <th>XP</th>
                      <th>Durum</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar">
                                {(user.username || '').substring(0, 2).toUpperCase()}
                              </div>
                              <span>{user.username}</span>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>{new Date(user.created_at || '').toLocaleDateString('tr-TR')}</td>
                          <td>{(user as any).level || 1}</td>
                          <td>{(user as any).total_xp || 0}</td>
                          <td>
                            <span className={`status-badge status-${(user as any).status === 'suspended' ? 'error' : 'success'}`}>
                              {(user as any).status === 'suspended' ? 'Askıda' : 'Aktif'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-secondary btn-sm" onClick={() => navigate(`/profile/${user.username}`)}>
                                Görüntüle
                              </button>
                              {(user as any).status === 'suspended' ? (
                                <button className="btn-secondary btn-sm" onClick={() => void handleActivateUser(Number(user.id))}>
                                  Aktive Et
                                </button>
                              ) : (
                                <button className="btn-secondary btn-sm" onClick={() => void handleSuspendUser(Number(user.id))}>
                                  Askıya Al
                                </button>
                              )}
                              <button className="btn-secondary btn-sm" style={{ color: '#ef4444' }} onClick={() => void handleDeleteUser(Number(user.id))}>
                                Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>
                          Kullanıcı bulunamadı
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div className="admin-tab">
              <div className="admin-header">
                <h1 className="admin-title">Projeler ({projects.length})</h1>
              </div>

              <div className="admin-card">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Proje</th>
                      <th>Tip</th>
                      <th>Görünürlük</th>
                      <th>Durum</th>
                      <th>Sahibi</th>
                      <th>Agentlar</th>
                      <th>Görevler</th>
                      <th>Oluşturulma</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.length > 0 ? (
                      projects.map((project) => (
                        <tr key={project.id}>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar">
                                {project.title.substring(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <div>{project.title}</div>
                                <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                                  {project.description?.substring(0, 50)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${project.project_type === 'research' ? 'badge-info' : 'badge-warning'}`}>
                              {project.project_type === 'research' ? 'Research' : 'Mission'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              project.visibility === 'public' ? 'badge-success' :
                              project.visibility === 'restricted' ? 'badge-warning' :
                              'badge-secondary'
                            }`}>
                              {project.visibility}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${project.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>
                              {project.status}
                            </span>
                          </td>
                          <td>
                            <div style={{ fontSize: '13px' }}>
                              <div>{project.owner_name}</div>
                              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                {project.owner_email}
                              </div>
                            </div>
                          </td>
                          <td>{project.agent_count}</td>
                          <td>{project.task_count}</td>
                          <td>{new Date(project.created_at).toLocaleDateString('tr-TR')}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="btn-icon"
                                title="Düzenle"
                                onClick={() => {
                                  // eslint-disable-next-line no-alert
                                  alert('Edit: ' + project.id)
                                }}
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-icon"
                                title={project.is_featured ? 'Öne Çıkarılmış' : 'Öne Çıkar'}
                                onClick={() => void handleFeatureProject(project.id, !project.is_featured)}
                              >
                                {project.is_featured ? '⭐' : '☆'}
                              </button>
                              <button
                                className="btn-icon btn-danger"
                                title="Sil"
                                onClick={() => void handleDeleteProject(project.id)}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                          Proje bulunamadı
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Agents Tab */}
          {activeTab === 'agents' && (
            <div className="admin-tab">
              <div className="admin-header">
                <h1 className="admin-title">Agentlar ({agents.length})</h1>
              </div>

              <div className="admin-card">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Tip</th>
                      <th>Oluşturulma</th>
                      <th>Etkileşim</th>
                      <th>Rating</th>
                      <th>Durum</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.length > 0 ? (
                      agents.map((agent) => (
                        <tr key={agent.id}>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar">
                                {(agent.name || agent.agent_name || 'A').substring(0, 2).toUpperCase()}
                              </div>
                              <span>{agent.name || agent.agent_name}</span>
                            </div>
                          </td>
                          <td>{agent.agent_type || agent.persona}</td>
                          <td>{new Date(agent.created_at).toLocaleDateString('tr-TR')}</td>
                          <td>{agent.total_interactions || 0}</td>
                          <td>{(agent.avg_rating || 0).toFixed(1)}</td>
                          <td>
                            <span className={`status-badge status-${agent.status === 'active' ? 'success' : 'error'}`}>
                              {agent.status === 'active' ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              {agent.status === 'active' ? (
                                <button className="btn-secondary btn-sm" onClick={() => void handleDeactivateAgent(agent.id)}>
                                  Deaktive Et
                                </button>
                              ) : (
                                <button className="btn-secondary btn-sm" onClick={() => void handleActivateAgent(agent.id)}>
                                  Aktive Et
                                </button>
                              )}
                              <button className="btn-secondary btn-sm" style={{ color: '#ef4444' }} onClick={() => void handleDeleteAgent(agent.id)}>
                                Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>
                          Agent bulunamadı
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Economy Tab */}
          {activeTab === 'economy' && (
            <div className="admin-tab">
              <h1 className="admin-title">Ekonomi</h1>

              {revenue ? (
                <>
                  <div className="stats-grid-3">
                    <div className="stat-card">
                      <div className="stat-card-value">${(revenue.total_revenue / 1000).toFixed(1)}K</div>
                      <div className="stat-card-label">Toplam FLZ</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-card-value">${(revenue.monthly_revenue / 1000).toFixed(1)}K</div>
                      <div className="stat-card-label">Bu Ay</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-card-value">${(revenue.platform_commission / 1000).toFixed(1)}K</div>
                      <div className="stat-card-label">Platform Komisyon</div>
                    </div>
                  </div>

                  <div className="admin-card">
                    <h3 className="card-title">FLZ İşlemleri</h3>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Kullanıcı</th>
                          <th>Miktar</th>
                          <th>Tip</th>
                          <th>Tarih</th>
                        </tr>
                      </thead>
                      <tbody>
                        {revenue.transactions.length > 0 ? (
                          revenue.transactions.map((tx) => (
                            <tr key={tx.id}>
                              <td>{tx.username}</td>
                              <td>${tx.amount}</td>
                              <td>{tx.type}</td>
                              <td>{new Date(tx.created_at).toLocaleDateString('tr-TR')}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>
                              İşlem bulunamadı
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          )}

          {/* Costs Tab */}
          {activeTab === 'costs' && (
            <div className="admin-tab">
              <h1 className="admin-title">Maliyet Yönetimi</h1>

              {costs ? (
                <>
                  <div className="stats-grid-3">
                    <div className="stat-card">
                      <div className="stat-card-value">${costs.llm_costs.toFixed(0)}</div>
                      <div className="stat-card-label">LLM Maliyeti</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-card-value">${costs.storage_costs.toFixed(0)}</div>
                      <div className="stat-card-label">Storage</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-card-value">${costs.bandwidth_costs.toFixed(0)}</div>
                      <div className="stat-card-label">Bandwidth</div>
                    </div>
                  </div>

                  <div className="admin-card">
                    <h3 className="card-title">LLM Provider Maliyetleri</h3>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Provider</th>
                          <th>Maliyet</th>
                          <th>İstek Sayısı</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costs.breakdown.length > 0 ? (
                          costs.breakdown.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.provider}</td>
                              <td>${item.cost.toFixed(2)}</td>
                              <td>{item.requests.toLocaleString()}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center', color: '#94a3b8' }}>
                              Maliyet verisi bulunamadı
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="admin-tab">
              <h1 className="admin-title">Ayarlar</h1>

              {/* Platform Settings */}
              <div className="admin-card">
                <h3 className="card-title">Platform Ayarları</h3>
                <div className="settings-form">
                  <div className="form-group">
                    <label>Platform Komisyon Oranı (%)</label>
                    <input type="number" defaultValue="10" />
                  </div>
                  <div className="form-group">
                    <label>Minimum Bağış (FLZ)</label>
                    <input type="number" defaultValue="100" />
                  </div>
                  <button className="btn-primary">Kaydet</button>
                </div>
              </div>

              {/* Payment Settings */}
              <div className="admin-card">
                <h3 className="card-title">Ödeme Yöntemleri</h3>
                <div className="settings-form">
                  <div className="form-group">
                    <label>Stripe API Key</label>
                    <input type="password" placeholder="sk_live_..." />
                  </div>
                  <div className="form-group">
                    <label>Stripe Webhook Secret</label>
                    <input type="password" placeholder="whsec_..." />
                  </div>
                  <div className="form-group">
                    <label>PayPal Client ID</label>
                    <input type="text" placeholder="AZoxxxxxxxxxxx" />
                  </div>
                  <div className="form-group">
                    <label>PayPal Client Secret</label>
                    <input type="password" placeholder="ELxxxxxxxxxxxxxx" />
                  </div>
                  <button className="btn-primary">Ödeme Yönetimini Kaydet</button>
                </div>
              </div>

              {/* Admin Wallet Management */}
              <div className="admin-card">
                <h3 className="card-title">Kullanıcı Cüzdan Yönetimi</h3>
                <div className="settings-form">
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>Kullanıcı ID</label>
                    <input type="number" placeholder="Kullanıcı ID girin" />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>Miktar ($)</label>
                    <input type="number" placeholder="Miktar" min="0" step="0.01" />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>Açıklama</label>
                    <textarea placeholder="Sebep (opsiyonel)" style={{ width: '100%', padding: '8px', borderRadius: '4px' }} rows={3} />
                  </div>
                  <button className="btn-primary">+ Bakiye Ekle</button>
                </div>
              </div>
            </div>
          )}

          {/* Waitlist Tab */}
          {activeTab === 'waitlist' && (
            <div className="admin-tab">
              <h1 className="admin-title">Waitlist ({waitlist.length})</h1>
              <div className="admin-card">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Email</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.map((w, i) => (
                      <tr key={w.email}>
                        <td>{i + 1}</td>
                        <td>{w.email}</td>
                        <td>{new Date(w.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {waitlist.length === 0 && (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No waitlist entries yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  )
}
